import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createCartCheckoutSession, getMoneyMotionEnabled } from '#bv/lib/moneymotion'
import { clearMmCheckoutContext, stashMmCheckoutContext } from '#bv/lib/mmCheckoutSession'
import { createManualFfOrder, getManualFfEnabled } from '#bv/lib/manualFf'
import { createNowPaymentsCartPayment, getNowPaymentsEnabled } from '#bv/lib/nowpayments'
import { cartItemsForCheckout, isFixedPackageCartLine, useCart, type CartItem } from '#bv/lib/cart'
import { validateRobuxGamepassLinkMessage } from '#bv/lib/robuxProduct'
import { validateDiscordServerLinkMessage } from '#bv/lib/serverBoostProduct'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { CheckoutCurrencyNotice } from '#bv/components/CheckoutCurrencyNotice'
import { useCurrency } from '#bv/lib/currency/CurrencyContext'
import { useProductStock } from '#bv/lib/useProductStock'
import {
  buyerEmailPresenceMessage,
  formatCheckoutError,
  isEmailRelatedCheckoutMessage,
  resolveBuyerEmail,
} from '#bv/lib/buyerEmail'
import { orderItemsToPasteSummary } from '#bv/lib/supportTicketPaste'
import { productImageUrl } from '#bv/lib/productImageUrl'
import products, { categories } from '#bv/data/products'

export const Route = createFileRoute('/cart')({
  validateSearch: (raw: Record<string, unknown>): { checkout?: '1' } => ({
    checkout: raw.checkout === '1' || raw.checkout === 1 ? '1' : undefined,
  }),
  component: CartPage,
})

function catalogProduct(productId: number) {
  return products.find((p) => p.id === productId)
}

function categoryLabelForProductId(productId: number): string {
  const p = catalogProduct(productId)
  if (!p) return 'Digital product'
  return categories.find((c) => c.id === p.category)?.label ?? 'Digital product'
}

function cartConfigValidationError(items: CartItem[]): string | null {
  for (const item of items) {
    if (item.serverBoostCount != null) {
      const err = validateDiscordServerLinkMessage(item.serverBoostInviteLink)
      if (err) return err
    }
    if (item.robuxAmount != null) {
      const err = validateRobuxGamepassLinkMessage(item.robuxGamepassLink)
      if (err) return err
    }
  }
  return null
}

function CartPage() {
  const navigate = useNavigate()
  const { formatUsdCharge } = useCurrency()
  const { checkout: checkoutIntent } = Route.useSearch()
  const checkoutPanelRef = useRef<HTMLDivElement>(null)
  const checkoutFormRef = useRef<HTMLFormElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const payTabInitialized = useRef(false)
  const { items, count, subtotal, remove, setQty, clear } = useCart()
  const [loading, setLoading] = useState(false)
  const [cryptoLoading, setCryptoLoading] = useState(false)
  const [mmEnabled, setMmEnabled] = useState<boolean | null>(null)
  const [cryptoEnabled, setCryptoEnabled] = useState<boolean | null>(null)
  const [manualLoading, setManualLoading] = useState(false)
  const [manualEnabled, setManualEnabled] = useState<boolean | null>(null)
  const [payCurrency, setPayCurrency] = useState('ltc')
  const [emailError, setEmailError] = useState('')
  const [payError, setPayError] = useState('')
  const [email, setEmail] = useState('')
  const [payTab, setPayTab] = useState<'card' | 'manual' | 'crypto'>('card')
  const { inStock } = useProductStock()
  const hasRobuxLine = items.some((i) => i.robuxAmount != null)
  const outOfStockItems = items.filter((item) => !inStock(item.productId))
  const hasOutOfStock = outOfStockItems.length > 0

  useEffect(() => {
    getManualFfEnabled()
      .then(setManualEnabled)
      .catch(() => setManualEnabled(false))
    getMoneyMotionEnabled()
      .then(setMmEnabled)
      .catch(() => setMmEnabled(false))
    getNowPaymentsEnabled()
      .then(setCryptoEnabled)
      .catch(() => setCryptoEnabled(false))
  }, [])

  useEffect(() => {
    if (payTabInitialized.current) return
    if (mmEnabled === null || manualEnabled === null || cryptoEnabled === null) return
    payTabInitialized.current = true
    if (mmEnabled) setPayTab('card')
    else if (manualEnabled) setPayTab('manual')
    else if (cryptoEnabled) setPayTab('crypto')
  }, [mmEnabled, manualEnabled, cryptoEnabled])

  useEffect(() => {
    if (!hasRobuxLine) return
    if (manualEnabled) {
      setPayTab('manual')
    } else if (cryptoEnabled) {
      setPayTab('crypto')
    }
  }, [hasRobuxLine, manualEnabled, cryptoEnabled])

  useEffect(() => {
    if (checkoutIntent !== '1' || items.length === 0) return
    const t = window.setTimeout(() => {
      checkoutPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
    return () => window.clearTimeout(t)
  }, [checkoutIntent, items.length])

  /** Browser autofill often skips React onChange — sync DOM → state once checkout is visible. */
  useEffect(() => {
    const el = emailInputRef.current
    if (!el) return
    const sync = () => {
      const v = el.value.trim()
      if (v) setEmail(v)
    }
    sync()
    const t = window.setTimeout(sync, 400)
    return () => window.clearTimeout(t)
  }, [checkoutIntent, items.length, payTab])

  /** FormData + DOM reads autofill even when React state is stale (common on mobile / in-app browsers). */
  const readCheckoutEmail = () => {
    const form = checkoutFormRef.current ?? emailInputRef.current?.form ?? null
    if (form) {
      const fromFd = new FormData(form).get('email')
      if (typeof fromFd === 'string' && fromFd.trim()) {
        return resolveBuyerEmail(fromFd, emailInputRef.current)
      }
      const named = form.elements.namedItem('email')
      if (named instanceof HTMLInputElement && named.value.trim()) {
        return resolveBuyerEmail(named.value, emailInputRef.current)
      }
    }
    const byId =
      typeof document !== 'undefined' ? document.getElementById('checkout-buyer-email') : null
    if (byId instanceof HTMLInputElement && byId.value.trim()) {
      return resolveBuyerEmail(byId.value, emailInputRef.current)
    }
    return resolveBuyerEmail(email, emailInputRef.current)
  }

  const prepareCheckoutEmail = (): string | null => {
    const buyerEmail = readCheckoutEmail()
    const display = emailInputRef.current?.value ?? buyerEmail
    if (display && display !== email) setEmail(display)
    const presenceErr = buyerEmailPresenceMessage(buyerEmail)
    if (presenceErr) {
      setEmailError(presenceErr)
      setPayError('')
      return null
    }
    setEmailError('')
    return buyerEmail
  }

  const applyCheckoutFailure = (err: unknown, fallback: string) => {
    const msg = formatCheckoutError(err, fallback)
    if (isEmailRelatedCheckoutMessage(msg)) {
      setEmailError(msg)
      setPayError('')
    } else {
      setPayError(msg)
      setEmailError('')
    }
  }

  const submitCheckout = (e: FormEvent) => {
    e.preventDefault()
    if (payTab === 'card' && mmEnabled) void startCardCheckout()
    else if (payTab === 'manual' && manualEnabled) void checkoutManualFf()
    else if (payTab === 'crypto' && cryptoEnabled) void checkoutCrypto()
  }

  const startCardCheckout = async () => {
    setEmailError('')
    setPayError('')
    if (hasRobuxLine) {
      setPayError('Robux orders can only be paid through PayPal F&F or crypto.')
      return
    }
    if (hasOutOfStock) {
      setPayError(`Cannot checkout. Out of stock: ${outOfStockItems.map((item) => item.name).join(', ')}`)
      return
    }
    const buyerEmail = prepareCheckoutEmail()
    if (!buyerEmail) return
    const configErr = cartConfigValidationError(items)
    if (configErr) {
      setPayError(configErr)
      return
    }
    setLoading(true)
    try {
      const result = await createCartCheckoutSession({ data: { items: cartItemsForCheckout(items), email: buyerEmail } })
      if (!result?.checkoutUrl) throw new Error('Checkout URL not returned')
      stashMmCheckoutContext(result.checkoutSessionId, buyerEmail, orderItemsToPasteSummary(items))
      window.location.href = result.checkoutUrl
    } catch (e) {
      applyCheckoutFailure(e, 'Unable to start checkout.')
    } finally {
      setLoading(false)
    }
  }

  const checkoutCrypto = async () => {
    setEmailError('')
    setPayError('')
    if (hasOutOfStock) {
      setPayError(`Cannot checkout. Out of stock: ${outOfStockItems.map((item) => item.name).join(', ')}`)
      return
    }
    const buyerEmail = prepareCheckoutEmail()
    if (!buyerEmail) return
    const configErr = cartConfigValidationError(items)
    if (configErr) {
      setPayError(configErr)
      return
    }
    clearMmCheckoutContext()
    setCryptoLoading(true)
    try {
      const p = await createNowPaymentsCartPayment({
        data: {
          email: buyerEmail,
          payCurrency,
          items: cartItemsForCheckout(items),
        },
      })
      if (!p.paymentId) throw new Error('No payment ID returned.')
      await navigate({
        to: '/checkout/crypto/$paymentId',
        params: { paymentId: p.paymentId },
      })
    } catch (e) {
      applyCheckoutFailure(e, 'Could not start crypto checkout.')
    } finally {
      setCryptoLoading(false)
    }
  }

  const checkoutManualFf = async () => {
    setEmailError('')
    setPayError('')
    if (hasOutOfStock) {
      setPayError(`Cannot checkout. Out of stock: ${outOfStockItems.map((item) => item.name).join(', ')}`)
      return
    }
    const buyerEmail = prepareCheckoutEmail()
    if (!buyerEmail) return
    const configErr = cartConfigValidationError(items)
    if (configErr) {
      setPayError(configErr)
      return
    }
    clearMmCheckoutContext()
    setManualLoading(true)
    try {
      const result = await createManualFfOrder({
        data: {
          email: buyerEmail,
          items: cartItemsForCheckout(items),
        },
      })
      window.location.href = `/checkout/success?manualOrderId=${encodeURIComponent(result.orderId)}`
    } catch (e) {
      applyCheckoutFailure(e, 'Unable to start manual F&F checkout.')
    } finally {
      setManualLoading(false)
    }
  }

  const paymentsReady = mmEnabled !== null && manualEnabled !== null && cryptoEnabled !== null
  const anyPayment = hasRobuxLine ? manualEnabled || cryptoEnabled : mmEnabled || manualEnabled || cryptoEnabled
  const paymentTabCount = hasRobuxLine
    ? Number(manualEnabled) + Number(cryptoEnabled)
    : Number(mmEnabled) + Number(manualEnabled) + Number(cryptoEnabled)
  const paymentTabGridClass =
    paymentTabCount <= 1 ? 'grid-cols-1' : paymentTabCount === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'linear-gradient(165deg, #080a12 0%, #050508 40%, #0c0e18 100%), radial-gradient(900px 420px at 100% 0%, rgba(0, 191, 255,0.06), transparent 55%)',
      }}
    >
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:max-w-[1200px]">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="m-0 text-[0.65rem] font-bold uppercase tracking-[0.22em]" style={{ color: '#5a6d8a' }}>
              PulseDigital
            </p>
            <h1 className="font-display mt-1 text-3xl tracking-wide text-[#f0f6ff] sm:text-4xl">Checkout</h1>
          </div>
          <Link
            to="/shop"
            className="btn-chrome rounded-xl px-4 py-2 text-sm"
          >
            ← Continue shopping
          </Link>
        </div>

        {checkoutIntent === '1' && items.length > 0 ? (
          <p
            className="mb-6 rounded-xl border border-[rgba(0, 191, 255,0.22)] bg-[rgba(0, 191, 255,0.07)] px-4 py-3 text-sm"
            style={{ color: '#d4e8ff' }}
          >
            Review your order, enter your email, then choose a payment method.
          </p>
        ) : null}

        {items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
            <p className="text-[#8ecfff]">Your cart is empty.</p>
            <Link to="/shop" className="btn-primary mt-5 inline-flex rounded-xl px-8 py-3 text-sm font-semibold">
              Browse products
            </Link>
          </div>
        ) : (
          <div className="lg:grid lg:min-h-[calc(100vh-12rem)] lg:grid-cols-[minmax(0,1.05fr)_minmax(340px,440px)] lg:overflow-hidden lg:rounded-2xl lg:border lg:border-white/[0.08] lg:shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
            {/* Order summary */}
            <section
              className="flex flex-col border-b border-white/[0.08] lg:border-b-0 lg:border-r"
              style={{
                background: 'linear-gradient(180deg, rgba(20,16,26,0.98) 0%, rgba(12,10,16,0.99) 100%)',
                borderColor: 'rgba(0,191,255,0.1)',
              }}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-white/[0.07] px-5 py-5 sm:px-6 sm:py-6">
                <h2 className="m-0 text-lg font-bold tracking-tight text-[#f0f6ff]">Order summary</h2>
                <span
                  className="rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(255,255,255,0.06)', color: '#5a6d8a' }}
                >
                  {count} item{count === 1 ? '' : 's'}
                </span>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
                {items.map((item) => {
                  const cat = categoryLabelForProductId(item.productId)
                  const img = productImageUrl(catalogProduct(item.productId)?.image ?? '/pulsedigital-logo.png')
                  const line = item.unitPrice * item.quantity
                  const ok = inStock(item.productId)
                  return (
                    <article className="relative flex flex-col gap-3 rounded-2xl border border-white/[0.08] bg-black/25 p-3 sm:flex-row sm:items-start sm:gap-4 sm:p-4">
                      <button
                        type="button"
                        aria-label="Remove item"
                        className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-[#5a6d8a] transition-colors hover:bg-white/10 hover:text-red-300 sm:right-3 sm:top-3"
                        onClick={() =>
                          remove(
                            item.productId,
                            item.unitPrice,
                            item.smmPackageQuantity,
                            item.smmTiktokTarget,
                            item.smmTierId,
                            item.robuxAmount,
                            item.robuxGamepassPrice,
                            item.robuxGamepassLink,
                            item.serverBoostCount,
                            item.serverBoostInviteLink,
                          )
                        }
                      >
                        ×
                      </button>
                      <img
                        src={img}
                        alt=""
                        className="h-[72px] w-[72px] shrink-0 rounded-xl border border-white/10 object-cover sm:h-20 sm:w-20"
                      />
                      <div className="min-w-0 flex-1 pr-8 sm:pr-2">
                        <h3 className="m-0 text-sm font-semibold leading-snug text-[#f0f6ff] sm:text-[0.95rem]">
                          {item.name}
                        </h3>
                        <p className="mt-0.5 text-xs" style={{ color: '#5a6d8a' }}>
                          {cat}
                        </p>
                        {item.smmTiktokTarget ? (
                          <p className="mt-1 text-xs text-[#9ec5ff]">
                            {item.smmPlatform ? `${item.smmPlatform.charAt(0).toUpperCase()}${item.smmPlatform.slice(1)}` : 'Target'}:{' '}
                            {item.smmTiktokTarget}
                          </p>
                        ) : null}
                        {item.robuxAmount != null ? (
                          <>
                            <p className="mt-1 text-xs text-[#d4e8ff]">
                              {item.robuxAmount.toLocaleString()} R$ after tax · gamepass{' '}
                              {item.robuxGamepassPrice?.toLocaleString() ?? '—'} R$
                            </p>
                            {item.robuxGamepassLink ? (
                              <p className="mt-1 break-all text-xs text-[#9ec5ff]">
                                Gamepass:{' '}
                                <a
                                  href={item.robuxGamepassLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#d4e8ff]"
                                >
                                  {item.robuxGamepassLink}
                                </a>
                              </p>
                            ) : null}
                          </>
                        ) : null}
                        {item.serverBoostCount != null ? (
                          <>
                            <p className="mt-1 text-xs text-[#c4b5fd]">
                              {item.serverBoostCount} server boost{item.serverBoostCount === 1 ? '' : 's'}
                            </p>
                            {item.serverBoostInviteLink ? (
                              <p className="mt-1 break-all text-xs text-[#9ec5ff]">
                                Discord server:{' '}
                                <a
                                  href={item.serverBoostInviteLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#d4e8ff]"
                                >
                                  {item.serverBoostInviteLink}
                                </a>
                              </p>
                            ) : null}
                          </>
                        ) : null}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span className="text-xs tabular-nums text-[#a8c4e8]">
                            <DisplayPrice usd={item.unitPrice} /> each
                          </span>
                          <div className="flex items-center rounded-lg border border-white/10 bg-black/30">
                            <button
                              type="button"
                              className="px-2.5 py-1 text-sm text-[#8ecfff] hover:bg-white/10 disabled:opacity-40"
                              disabled={item.quantity <= 1 || isFixedPackageCartLine(item)}
                              onClick={() =>
                                setQty(
                                  item.productId,
                                  item.unitPrice,
                                  item.quantity - 1,
                                  item.smmPackageQuantity,
                                  item.smmTiktokTarget,
                                  item.smmTierId,
                                  item.robuxAmount,
                                  item.robuxGamepassPrice,
                                  item.robuxGamepassLink,
                                  item.serverBoostCount,
                                  item.serverBoostInviteLink,
                                )
                              }
                            >
                              −
                            </button>
                            <span className="min-w-[2rem] text-center text-sm font-medium tabular-nums text-[#f0f6ff]">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              className="px-2.5 py-1 text-sm text-[#8ecfff] hover:bg-white/10"
                              disabled={isFixedPackageCartLine(item)}
                              onClick={() =>
                                setQty(
                                  item.productId,
                                  item.unitPrice,
                                  item.quantity + 1,
                                  item.smmPackageQuantity,
                                  item.smmTiktokTarget,
                                  item.smmTierId,
                                  item.robuxAmount,
                                  item.robuxGamepassPrice,
                                  item.robuxGamepassLink,
                                  item.serverBoostCount,
                                  item.serverBoostInviteLink,
                                )
                              }
                            >
                              +
                            </button>
                          </div>
                          <span className={`text-[0.65rem] font-bold uppercase tracking-wide ${ok ? 'text-emerald-300' : 'text-red-300'}`}>
                            {ok ? 'In stock' : 'Out of stock'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between border-t border-white/[0.06] pt-2 sm:ml-auto sm:flex-col sm:items-end sm:border-t-0 sm:pt-0 sm:pr-6">
                        <span className="text-xs sm:hidden" style={{ color: '#5a6d8a' }}>
                          Line total
                        </span>
                        <p className="m-0 text-base font-semibold tabular-nums text-[#f0f6ff] sm:text-lg">
                          <DisplayPrice usd={line} />
                        </p>
                      </div>
                    </article>
                  )
                })}
              </div>

              <div className="mt-auto space-y-3 border-t border-white/[0.07] px-5 py-5 sm:px-6">
                <div
                  className="flex items-start gap-3 rounded-xl border border-white/[0.08] px-4 py-3"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <span className="text-lg leading-none" aria-hidden>
                    ✦
                  </span>
                  <p className="m-0 text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
                    Digital delivery — support shares fulfillment details after payment clears. Card checkouts may include
                    small processor fees; your charged total can be slightly above the cart subtotal.
                  </p>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt style={{ color: '#5a6d8a' }}>Subtotal</dt>
                    <dd className="tabular-nums font-medium text-[#f0f6ff]">
                      <DisplayPrice usd={subtotal} />
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt style={{ color: '#5a6d8a' }}>Delivery</dt>
                    <dd className="font-medium text-emerald-300/90">Free</dd>
                  </div>
                </dl>
                <div className="flex items-end justify-between gap-4 border-t border-white/[0.08] pt-4">
                  <span className="text-sm font-bold uppercase tracking-wider" style={{ color: '#5a6d8a' }}>
                    Total
                  </span>
                  <span className="font-display text-3xl tracking-wide text-[#f0f6ff]">
                    <DisplayPrice usd={subtotal} />
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => clear()}
                  className="w-full rounded-xl border border-dashed border-white/15 py-2.5 text-xs font-semibold text-[#5a6d8a] hover:border-white/25 hover:text-[#8ecfff]"
                >
                  Clear cart
                </button>
              </div>
            </section>

            {/* Payment */}
            <section
              ref={checkoutPanelRef}
              id="checkout-panel"
              className="scroll-mt-28 flex flex-col px-5 py-6 sm:px-7 sm:py-8"
              style={{
                background:
                  'radial-gradient(700px 360px at 50% -10%, rgba(0, 191, 255,0.07), transparent 50%), linear-gradient(180deg, #08060b 0%, #050508 100%)',
              }}
            >
              <div className="mb-2 flex items-center gap-2">
                <img
                  src="/pulsedigital-logo-mark.png"
                  alt=""
                  className="h-9 w-auto object-contain"
                  style={{ filter: 'drop-shadow(0 0 8px rgba(0, 191, 255,0.35)) drop-shadow(0 0 12px rgba(123, 47, 190,0.25))' }}
                />
                <span className="font-display text-lg tracking-widest chrome-text">PulseDigital</span>
              </div>
              <p className="mt-1 text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
                Secure checkout — choose how you want to pay.
              </p>

              {!paymentsReady ? (
                <div className="mt-10 flex items-center gap-3 text-sm" style={{ color: '#5a6d8a' }}>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#00bfff] border-t-transparent" />
                  Loading payment options…
                </div>
              ) : !anyPayment ? (
                <p className="mt-6 text-sm text-red-300">
                  {hasRobuxLine
                    ? 'Robux orders require PayPal F&F or crypto, but neither path is configured right now.'
                    : 'Checkout unavailable. Configure MoneyMotion, crypto payments, or PayPal F&F on the server.'}
                </p>
              ) : (
                <>
                  <div className={`mt-6 grid gap-2 ${paymentTabGridClass}`}>
                    {mmEnabled && !hasRobuxLine ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPayTab('card')
                          setEmailError('')
                          setPayError('')
                        }}
                        className={`rounded-xl px-3 py-3 text-center text-sm font-semibold transition-all ${
                          payTab === 'card'
                            ? 'text-[#f0f6ff]'
                            : 'border border-white/10 bg-black/20 text-[#5a6d8a] hover:border-white/20 hover:text-[#8ecfff]'
                        }`}
                        style={
                          payTab === 'card'
                            ? {
                                background: 'rgba(0, 191, 255,0.18)',
                                boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.35)',
                              }
                            : undefined
                        }
                      >
                        Card
                      </button>
                    ) : null}
                    {manualEnabled ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPayTab('manual')
                          setEmailError('')
                          setPayError('')
                        }}
                        className={`rounded-xl px-3 py-3 text-center text-sm font-semibold transition-all ${
                          payTab === 'manual'
                            ? 'text-[#f0f6ff]'
                            : 'border border-white/10 bg-black/20 text-[#5a6d8a] hover:border-white/20 hover:text-[#8ecfff]'
                        }`}
                        style={
                          payTab === 'manual'
                            ? {
                                background: 'rgba(0, 191, 255,0.18)',
                                boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.35)',
                              }
                            : undefined
                        }
                      >
                        PayPal F&amp;F
                      </button>
                    ) : null}
                    {cryptoEnabled ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPayTab('crypto')
                          setEmailError('')
                          setPayError('')
                        }}
                        className={`rounded-xl px-3 py-3 text-center text-sm font-semibold transition-all ${
                          payTab === 'crypto'
                            ? 'text-[#f0f6ff]'
                            : 'border border-white/10 bg-black/20 text-[#5a6d8a] hover:border-white/20 hover:text-[#8ecfff]'
                        }`}
                        style={
                          payTab === 'crypto'
                            ? {
                                background: 'rgba(0, 191, 255,0.18)',
                                boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.35)',
                              }
                            : undefined
                        }
                      >
                        Crypto
                      </button>
                    ) : null}
                  </div>

                  <form ref={checkoutFormRef} className="mt-6" noValidate onSubmit={submitCheckout}>
                  <label className="block" htmlFor="checkout-buyer-email">
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#5a6d8a' }}>
                      Email
                    </span>
                    <input
                      ref={emailInputRef}
                      id="checkout-buyer-email"
                      name="email"
                      type="email"
                      inputMode="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setEmailError('')
                        setPayError('')
                      }}
                      onInput={(e) => {
                        setEmail(e.currentTarget.value)
                        setEmailError('')
                        setPayError('')
                      }}
                      className="bv-input mt-2 w-full rounded-xl px-4 py-3 text-sm"
                      placeholder="you@example.com"
                      autoComplete="email"
                      aria-describedby={emailError ? 'checkout-email-error' : undefined}
                    />
                  </label>

                  {emailError ? (
                    <p id="checkout-email-error" className="mt-3 text-sm text-red-300" role="alert">
                      {emailError}
                    </p>
                  ) : null}

                  <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-white/[0.07] pt-4 text-sm">
                    <span style={{ color: '#5a6d8a' }}>Due today</span>
                    <span className="font-display text-xl tabular-nums text-[#f0f6ff]">
                      <DisplayPrice usd={subtotal} />
                    </span>
                  </div>

                  <CheckoutCurrencyNotice usdTotal={subtotal} className="mt-3" />

                  {payError ? (
                    <p className="mt-3 text-sm text-red-300" role="alert">
                      {payError}
                    </p>
                  ) : null}

                  {payTab === 'card' && mmEnabled ? (
                    <div className="mt-5 space-y-4">
                      <p className="text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
                        You&apos;ll complete card payment on our provider&apos;s secure page. Fees shown there are final.
                      </p>
                      <button
                        type="submit"
                        className="btn-primary flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold disabled:opacity-60"
                        disabled={loading || manualLoading || cryptoLoading || hasOutOfStock}
                      >
                        <svg className="h-4 w-4 shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path
                            d="M7 11V8a5 5 0 0110 0v3M6 11h12v10H6V11z"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {loading ? 'Redirecting…' : `Continue — ${formatUsdCharge(subtotal)}`}
                      </button>
                    </div>
                  ) : null}

                  {payTab === 'manual' && manualEnabled ? (
                    <div className="mt-5 space-y-4">
                      <p className="text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
                        Manual Friends &amp; Family flow — you&apos;ll get instructions to send payment, then support confirms
                        your order.
                      </p>
                      <button
                        type="submit"
                        className="btn-chrome w-full rounded-xl py-3.5 text-sm font-semibold disabled:opacity-60"
                        disabled={manualLoading || loading || cryptoLoading || hasOutOfStock}
                      >
                        {manualLoading ? 'Preparing order…' : 'Create PayPal F&F order'}
                      </button>
                    </div>
                  ) : null}

                  {payTab === 'crypto' && cryptoEnabled ? (
                    <div className="mt-5 space-y-4">
                      <label className="block">
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#5a6d8a' }}>
                          Pay with
                        </span>
                        <select
                          value={payCurrency}
                          onChange={(e) => setPayCurrency(e.target.value)}
                          className="bv-input mt-2 w-full rounded-xl px-4 py-3 text-sm"
                          aria-label="Cryptocurrency"
                        >
                          <option value="ltc">Litecoin (LTC)</option>
                          <option value="btc">Bitcoin (BTC)</option>
                          <option value="eth">Ethereum (ETH)</option>
                          <option value="doge">Dogecoin (DOGE)</option>
                          <option value="sol">Solana (SOL)</option>
                          <option value="usdt">USDT (ERC20)</option>
                        </select>
                      </label>
                      <button
                        type="submit"
                        className="w-full rounded-xl border border-[rgba(168,85,247,0.4)] bg-[rgba(168,85,247,0.14)] py-3.5 text-sm font-semibold text-violet-100 transition-colors hover:bg-[rgba(168,85,247,0.22)] disabled:cursor-wait disabled:opacity-60"
                        disabled={cryptoLoading || loading || manualLoading || hasOutOfStock}
                      >
                        {cryptoLoading ? 'Creating invoice…' : `Pay ${formatUsdCharge(subtotal)} with crypto`}
                      </button>
                      <p className="text-[0.7rem] leading-relaxed" style={{ color: '#5a6d8a' }}>
                        A crypto invoice is created for your order. Send the exact amount shown on the next screen.
                      </p>
                    </div>
                  ) : null}

                  <div className="mt-auto flex flex-wrap justify-center gap-x-5 gap-y-2 pt-10 text-[0.7rem]" style={{ color: '#5a6d8a' }}>
                    <Link to="/terms" className="hover:text-[#5a6d8a]">
                      Terms
                    </Link>
                    <Link to="/refund" className="hover:text-[#5a6d8a]">
                      Refunds
                    </Link>
                    <Link to="/contact" className="hover:text-[#5a6d8a]">
                      Contact
                    </Link>
                  </div>
                  </form>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}